import { useMemo } from 'react';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import { statusOf, TYRE_STATES } from './vehicleDiagram/diagramTheme';
import { describePosition } from './vehicleDiagram/positionDescription';

const CELL = { width: 44, height: 54 };
const TRACK = { outer: 94, inner: 44, sole: 56 };
const AXLE_GAP = 14;
const NOSE = 34;
const TAIL = 20;

const sideOf = (position, fallbackIndex) => {
  const side = String(position.side || '').toLowerCase();
  if (side === 'left' || side === 'right') return side;
  return Number(position.x) < 0 || fallbackIndex % 2 === 0 ? 'left' : 'right';
};

const slotOf = (position) => {
  const fitment = String(position.fitment_type || '').toLowerCase();
  return fitment.includes('inner') ? 'inner' : 'outer';
};

const mapGeometry = (positions) => {
  const axleNumbers = [...new Set(positions.map((position) => Number(position.axle_index) || 1))]
    .sort((a, b) => a - b);
  const hasDual = positions.some((position) => ['inner', 'outer'].includes(slotOf(position)))
    && axleNumbers.some((axle) => positions.filter((position) => Number(position.axle_index || 1) === axle).length > 2);
  const centreX = (hasDual ? TRACK.outer : TRACK.sole) + CELL.width / 2;
  const rowTop = (axleIndex) => NOSE + axleIndex * (CELL.height + AXLE_GAP);
  const cells = positions.map((position, index) => {
    const axle = Number(position.axle_index) || 1;
    const axleIndex = axleNumbers.indexOf(axle);
    const side = sideOf(position, index);
    const magnitude = !hasDual ? TRACK.sole : slotOf(position) === 'inner' ? TRACK.inner : TRACK.outer;
    return {
      position,
      left: centreX + (side === 'left' ? -magnitude : magnitude) - CELL.width / 2,
      top: rowTop(axleIndex),
    };
  });
  const axles = axleNumbers.map((axle, axleIndex) => {
    const members = cells.filter((cell) => Number(cell.position.axle_index || 1) === axle);
    const halfSpan = Math.max(...members.map((cell) => Math.abs((cell.left + CELL.width / 2) - centreX))) + CELL.width / 2;
    return { axle, top: rowTop(axleIndex) + CELL.height / 2, halfSpan };
  });
  const height = NOSE + axleNumbers.length * CELL.height + Math.max(0, axleNumbers.length - 1) * AXLE_GAP + TAIL;
  const innerOffset = hasDual ? TRACK.inner : TRACK.sole;
  const chassisHalf = Math.min(22, Math.max(8, innerOffset - CELL.width / 2 - 3));
  return {
    width: centreX * 2,
    height,
    centreX,
    cells,
    axles,
    chassis: { left: centreX - chassisHalf, top: 5, width: chassisHalf * 2, height: height - 10 },
    cab: { left: centreX - chassisHalf - 8, top: 1, width: chassisHalf * 2 + 16, height: 27 },
  };
};

const ClaudePositionMap = ({ positions, selectedPositionId, onSelectPosition, vehicleNumber }) => {
  const geometry = useMemo(() => mapGeometry(positions), [positions]);

  if (!positions.length) return <p className="tm-claude-map-empty">No tyre positions configured.</p>;

  return (
    <ClassNames>
      {({ css }) => (
        <div className="tm-claude-map">
      <div className="tm-claude-map__orientation" aria-hidden="true">
        <span>Left</span><strong>Front ↑</strong><span>Right</span>
      </div>
          <div
            className={`tm-claude-map__canvas ${css({ width: geometry.width, height: geometry.height })}`}
        role="group"
        aria-label={`${vehicleNumber || 'Vehicle'} tyre position map`}
      >
            <span className={`tm-claude-map__chassis ${css(geometry.chassis)}`} />
            <span className={`tm-claude-map__cab ${css(geometry.cab)}`} />
        {geometry.axles.map((axle) => (
          <span
            key={axle.axle}
            className={`tm-claude-map__axle ${css({ left: geometry.centreX - axle.halfSpan, top: axle.top - 2, width: axle.halfSpan * 2 })}`}
          />
        ))}
        {geometry.cells.map(({ position, left, top }) => {
          const state = statusOf(position);
          const presentation = TYRE_STATES[state];
          const selected = String(position.id) === String(selectedPositionId);
          return (
            <button
              key={position.id}
              type="button"
              data-empty={state === 'empty' || undefined}
              data-selected={selected || undefined}
              aria-pressed={selected}
              aria-label={describePosition(position)}
              title={describePosition(position)}
              className={`tm-claude-map__tyre ${css({ left, top, '--tyre-color': presentation.color, '--tyre-edge': presentation.edge })}`}
              onClick={() => onSelectPosition(position)}
            >
              <span className="tm-claude-map__mark" aria-hidden="true">{presentation.mark}</span>
              <span className="tm-claude-map__code">{position.code}</span>
            </button>
          );
        })}
          </div>
        </div>
      )}
    </ClassNames>
  );
};

ClaudePositionMap.propTypes = {
  positions: PropTypes.array.isRequired,
  selectedPositionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectPosition: PropTypes.func.isRequired,
  vehicleNumber: PropTypes.string,
};

export default ClaudePositionMap;
